import React, { useMemo, useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Chip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getDisplayName } from "./utils/statusLabels";
import { getMaxQtyForItem } from "./utils/materialRequestConstants";
import ConfirmDialog from "./components/ConfirmDialog";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { getCurrentProfile, isBoxAccount } from "./utils/userProfile";

// ✅ نفس فكرة USER_TO_EMPLOYEE بصفحة طلب الإجازة - نقرأ هوية الطالب من الرابط
// (?u=...) بدل نص حر، حتى نضمن كل طلب يوصل باسم صحيح
const USER_TO_EMPLOYEE = {
  "art@1234": "برهان",
  "mohamed@123": "محمد",
  "halima@123": "حليمة",
};

const ROLE_BY_USER = {
  "art@1234": "workshop",
  "mohamed@123": "illustrator",
  "halima@123": "illustrator",
};

// 🧰 قائمة مواد الورشة الثابتة - بطلب المستخدم بالضبط
const WORKSHOP_MATERIALS = [
  { group: null, label: "ببلز قياس متر ونص (1.5 متر)" },
  { group: null, label: "ببلز قياس واحد متر (1 متر)" },
  { group: null, label: "خشب" },
  { group: null, label: "لاصق شفاف عريض" },
  { group: null, label: "علبة كلبس دوشمه" },
  { group: null, label: "علبة بسامير كبس" },
  { group: null, label: "علبة جويات + براغي 6 ملم" },
  { group: null, label: "علبة جويات + براغي 8 ملم" },
  { group: null, label: "برينة دريل همر 5 ملم" },
  { group: null, label: "برينة دريل همر 6 ملم" },
  { group: null, label: "برينة دريل همر 8 ملم" },
  { group: null, label: "برينة دريل شحن كونكريت 5 ملم" },
  { group: null, label: "برينة دريل شحن كونكريت 6 ملم" },
  { group: null, label: "برينة دريل شحن كونكريت 8 ملم" },
];

// 🎨 قائمة مواد الرسام الثابتة - بطلب المستخدم بالضبط، مقسّمة لمجاميع (السائل /
// المعجون أو العجينة) لعرضها بترويسات مفصولة
const ILLUSTRATOR_MATERIALS = [
  { group: null, label: "سيت فرش (15 قطعة) (القياس من 1 الى 5)" },
  { group: null, label: "سيت سكاكين (5 قطع) (القياس من 1 الى 5)" },
  { group: null, label: "ورق ذهب (25 ورقة)" },
  { group: null, label: "ورق ذهب (50 ورقة)" },
  { group: null, label: "وارنيش" },
  { group: "السائل", label: "بطل اكرلك سائل لون أبيض حجم 500 مل – شركة ماريس" },
  { group: "السائل", label: "بطل اكرلك سائل لون أسود حجم 500 مل – شركة ماريس" },
  { group: "السائل", label: "بطل اكرلك سائل لون أصفر حجم 500 مل – شركة ماريس" },
  { group: "السائل", label: "بطل اكرلك سائل لون برتقالي حجم 500 مل – شركة ماريس" },
  { group: "السائل", label: "بطل اكرلك سائل لون يلو ميد حجم 500 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون أزرق حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون أخضر حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون أحمر حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون جوزي حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون ذهبي حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون بيجي بشرة حجم 275 مل – شركة ماريس" },
  { group: "المعجون أو العجينة", label: "علبة اكرلك عجينة لون أبيض حجم 1200 مل" },
];

const MATERIALS_BY_ROLE = {
  workshop: WORKSHOP_MATERIALS,
  illustrator: ILLUSTRATOR_MATERIALS,
};

export default function MaterialRequestForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const returnTo = params.get("returnTo") || (isBoxAccount() ? "/workspace" : "/");
  // الرابط الثابت (?u=) من لوحات الورشة/الرسام، وإلا (بوكس "طلب مواد" ممنوح
  // من صلاحية المستخدمين) الحساب الحالي باسمه الظاهر
  const profile = getCurrentProfile();
  const usernameFromUrl = params.get("u") || (USER_TO_EMPLOYEE[profile.username] ? profile.username : "");
  const requesterUser = usernameFromUrl || profile.username || "";
  const employee = USER_TO_EMPLOYEE[usernameFromUrl] || (usernameFromUrl ? "" : profile.displayName);
  const fixedRole = params.get("role") || ROLE_BY_USER[usernameFromUrl] || "";
  // حساب بالبوكس يختار نوع المواد (ورشة/رسام) لأن ما إله قائمة ثابتة
  const [chosenRole, setChosenRole] = useState("");
  const role = fixedRole || chosenRole;
  const materials = MATERIALS_BY_ROLE[role] || [];

  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);

  useEffect(() => {
    // ✅ إعادة ضبط النموذج إذا انبنت الصفحة من جديد لمستخدم مختلف
    setQuantities({});
    setNotes("");
  }, [usernameFromUrl]);

  const handleRequestSubmit = () => {
    setError("");

    if (!employee || !role) {
      setError("❌ رابط الطلب غير صحيح (تأكد من زر الدخول).");
      return;
    }

    const items = [];
    materials.forEach((mat) => {
      const qty = Math.min(
        Number(quantities[mat.label] || 0),
        getMaxQtyForItem(mat.label)
      );
      if (qty > 0) {
        items.push({ label: mat.label, group: mat.group, quantity: qty });
      }
    });

    const trimmedNotes = notes.trim();

    if (items.length === 0 && !trimmedNotes) {
      setError("❌ حدد كمية لمادة وحدة على الأقل أو اكتب ملاحظة قبل الإرسال.");
      return;
    }

    setPendingItems(items);
    setConfirmOpen(true);
  };

  const performSubmit = async () => {
    const trimmedNotes = notes.trim();

    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_BASE}/material-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role,
          requestedBy: employee,
          requestedByUser: requesterUser,
          items: pendingItems,
          notes: trimmedNotes,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل إرسال طلب المواد");
      }

      setQuantities({});
      setNotes("");
      setPendingItems([]);
      setConfirmOpen(false);
      setSuccessOpen(true);
    } catch (e) {
      console.error(e);
      setError("❌ صار خطأ أثناء إرسال الطلب.");
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle =
    role === "illustrator" ? "🎨 طلبات مواد الرسام" : role ? "🧰 طلبات مواد الورشة" : "🧰 طلب مواد";

  let currentGroup = null;

  return (
    <Container dir="rtl" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h4" fontWeight={900}>
            {pageTitle}
          </Typography>

          <Chip
            label={`الطالب: ${getDisplayName(employee) || "—"}`}
            color="primary"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(returnTo)}
            sx={{ fontWeight: 800, borderRadius: 2, px: 3 }}
          >
            ⬅️ رجوع
          </Button>
        </Box>

        {employee && !fixedRole && (
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Typography fontWeight={800} sx={{ alignSelf: "center" }}>
              نوع المواد:
            </Typography>
            {[
              { value: "workshop", label: "🧰 مواد الورشة" },
              { value: "illustrator", label: "🎨 مواد الرسام" },
            ].map((opt) => (
              <Button
                key={opt.value}
                variant={chosenRole === opt.value ? "contained" : "outlined"}
                onClick={() => {
                  setChosenRole(opt.value);
                  setQuantities({});
                }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>
        )}

        {employee && !role ? (
          <Alert severity="info">اختار نوع المواد حتى تظهر القائمة.</Alert>
        ) : !employee || !role ? (
          <Alert severity="error">
            ❌ رابط الطلب غير صحيح (تأكد من زر الدخول من لوحتك).
          </Alert>
        ) : (
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              {materials.map((mat, idx) => {
                const showGroupHeader = mat.group !== currentGroup;
                currentGroup = mat.group;

                const itemMaxQty = getMaxQtyForItem(mat.label);

                return (
                  <React.Fragment key={mat.label}>
                    {showGroupHeader && mat.group && (
                      <Box mt={idx === 0 ? 0 : 1}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography fontWeight={900} color="primary">
                          {mat.group}
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" gap={2} alignItems="center">
                      <Typography sx={{ flex: 1 }}>{mat.label}</Typography>
                      <TextField
                        label="العدد"
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: itemMaxQty }}
                        value={quantities[mat.label] || ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === "") {
                            setQuantities((prev) => ({ ...prev, [mat.label]: "" }));
                            return;
                          }
                          const clamped = Math.min(
                            Math.max(Number(raw) || 0, 0),
                            itemMaxQty
                          );
                          setQuantities((prev) => ({
                            ...prev,
                            [mat.label]: String(clamped),
                          }));
                        }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  </React.Fragment>
                );
              })}

              <Divider sx={{ my: 1 }} />
              <Typography fontWeight={800} mb={1}>
                ملاحظات / أو طلبات أخرى
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>

            {error && (
              <Box mt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <Box mt={3}>
              <Button
                variant="contained"
                fullWidth
                sx={{ py: 1.3, fontWeight: 900, borderRadius: 2.5, fontSize: 16 }}
                onClick={handleRequestSubmit}
                disabled={submitting}
              >
                {submitting ? "جارٍ الإرسال..." : "📤 طلب"}
              </Button>
            </Box>
          </Paper>
        )}

        <ConfirmDialog
          open={confirmOpen}
          title="تأكيد إرسال الطلب"
          description="راجع المواد والكميات قبل الإرسال، هذا الطلب سيوصل مباشرة للمدير وبنين."
          confirmLabel="تأكيد الإرسال"
          cancelLabel="رجوع للتعديل"
          loading={submitting}
          onConfirm={performSubmit}
          onCancel={() => setConfirmOpen(false)}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mb: 1,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {pendingItems.map((item, idx) => (
              <Typography key={idx} fontSize={14}>
                • {item.label} — الكمية: <strong>{item.quantity}</strong>
              </Typography>
            ))}
            {notes.trim() && (
              <Typography fontSize={14} sx={{ mt: 1 }}>
                📝 ملاحظات: {notes.trim()}
              </Typography>
            )}
          </Box>
        </ConfirmDialog>

        <Snackbar
          open={successOpen}
          autoHideDuration={2500}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" variant="filled">
            ✅ تم إرسال طلب المواد للمدير وبنين
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
